import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryDropdown from '@/components/feature/CategoryDropdown';
import SEOArticleSection from '@/components/feature/SEOArticleSection';
import { categories } from '@/mocks/categories';

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20streetwear%20fashion%20editorial%20photography%20with%20sneakers%20hoodies%20and%20accessories%20arranged%20diagonally%20on%20a%20warm%20cream%20studio%20backdrop%2C%20soft%20golden%20lighting%20with%20gentle%20shadows%2C%20high-end%20e-commerce%20hero%20composition%20with%20generous%20negative%20space%2C%20vibrant%20yet%20harmonious%20color%20palette&width=1600&height=900&seq=hero-bg&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
          <div className="max-w-4xl">
            <p className="text-sm md:text-base font-semibold text-accent-400 tracking-wider uppercase mb-4">
              {t('hero.eyebrow')}
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.h1')}
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mb-8">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <span>{t('hero.cta.primary')}</span>
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a
                href="#guides"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold border border-white/30 rounded-lg hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                <span>{t('hero.cta.secondary')}</span>
                <i className="ri-book-open-line w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>

            <p className="text-sm text-white/60">
              <i className="ri-shield-check-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
              {t('hero.trust')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Search & Dropdown */}
      <section className="w-full py-12 md:py-16 bg-white border-b border-background-200/60">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t('search.h2')}
            </h2>
          </div>
          <CategoryDropdown />
        </div>
      </section>

      {/* Category Buttons Grid */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
            {t('categories.h2')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col items-center gap-2 p-3 md:p-4 bg-white rounded-xl border border-background-200/60 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs font-medium text-foreground-800 text-center group-hover:text-primary-500 transition-colors line-clamp-1">
                  {t(cat.labelKey)}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="w-full py-16 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
            {t('benefits.h2')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: 'ri-search-2-line', title: t('benefits.findProducts'), desc: t('benefits.findProductsDesc'), color: 'bg-primary-100 text-primary-500' },
              { icon: 'ri-user-search-line', title: t('benefits.compareSellers'), desc: t('benefits.compareSellersDesc'), color: 'bg-accent-100 text-accent-500' },
              { icon: 'ri-shield-check-line', title: t('benefits.checkQC'), desc: t('benefits.checkQCDesc'), color: 'bg-secondary-100 text-secondary-500' },
              { icon: 'ri-truck-line', title: t('benefits.understandShipping'), desc: t('benefits.understandShippingDesc'), color: 'bg-primary-50 text-primary-400' },
            ].map((item) => (
              <div key={item.title} className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center mb-4`}>
                  <i className={`${item.icon} w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
            {t('howItWorks.h2')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              { step: '1', title: t('howItWorks.step1'), desc: t('howItWorks.step1Desc') },
              { step: '2', title: t('howItWorks.step2'), desc: t('howItWorks.step2Desc') },
              { step: '3', title: t('howItWorks.step3'), desc: t('howItWorks.step3Desc') },
              { step: '4', title: t('howItWorks.step4'), desc: t('howItWorks.step4Desc') },
              { step: '5', title: t('howItWorks.step5'), desc: t('howItWorks.step5Desc') },
            ].map((item) => (
              <div key={item.step} className="relative text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-primary-500 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-1">{item.title}</h3>
                <p className="text-sm text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="w-full py-16 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {t('trust.h2')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto">
            {t('trust.text')}
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-foreground-500">
            <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
            <span>Conteúdo independente — não somos afiliados oficiais da CSSBuy.</span>
          </div>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="w-full py-12 md:py-16 bg-primary-50">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 text-white font-bold text-base rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap shadow-sm"
            >
              <i className="ri-external-link-line w-5 h-5 flex items-center justify-center"></i>
              {t('button.cssbuySpreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 text-white font-bold text-base rounded-xl hover:bg-accent-600 transition-colors whitespace-nowrap shadow-sm"
            >
              <i className="ri-shopping-bag-line w-5 h-5 flex items-center justify-center"></i>
              {t('button.buyItNow')}
            </a>
          </div>
        </div>
      </section>

      {/* SEO Articles Section */}
      <SEOArticleSection />

      {/* Final CTA */}
      <section className="w-full py-16 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {t('finalCta.h2')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('finalCta.text')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('finalCta.primary')}
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3.5 border-2 border-primary-500 text-primary-500 font-semibold rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              {t('finalCta.secondary')}
              <i className="ri-external-link-line w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}